// v10/src/components/admin/AdminImages.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Upload, Image, Trash2, Copy, Check, Sparkles,
  X, AlertCircle, FolderOpen, Wand2, Save,
  RefreshCw, CheckCircle2, Plane,
} from 'lucide-react';
import { aircraftService } from '../../services/apiService';
import api from '../../services/apiService';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../common/ConfirmModal';

const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload  = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const CopyBtn = ({ text, darkMode }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <motion.button onClick={copy} whileTap={{ scale: 0.9 }}
      style={{ padding: '5px 8px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: copied ? 'rgba(34,197,94,0.15)' : darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: copied ? '#22c55e' : darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600 }}>
      {copied ? <Check style={{ width: '12px', height: '12px' }} /> : <Copy style={{ width: '12px', height: '12px' }} />}
      {copied ? 'Copiado' : 'Copiar URL'}
    </motion.button>
  );
};

const ImageCard = ({ img, onDelete, darkMode }) => {
  const [preview, setPreview] = useState(false);
  return (
    <>
      <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
        style={{ borderRadius: '16px', overflow: 'hidden', background: darkMode ? 'rgba(255,255,255,0.04)' : 'white', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div onClick={() => setPreview(true)} style={{ height: '120px', cursor: 'pointer', overflow: 'hidden', background: darkMode ? '#1e293b' : '#f8fafc' }}>
          <img src={img.url} alt={img.publicId}
            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
        </div>
        <div style={{ padding: '10px 12px' }}>
          <p style={{ margin: '0 0 8px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {img.publicId?.split('/').pop() || 'imagen'}
          </p>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <CopyBtn text={img.url} darkMode={darkMode} />
            <motion.button onClick={() => onDelete(img)} whileTap={{ scale: 0.9 }}
              style={{ padding: '5px 6px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
              <Trash2 style={{ width: '12px', height: '12px' }} />
            </motion.button>
          </div>
        </div>
      </motion.div>
      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPreview(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <motion.img src={img.url} initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}
              style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '16px', objectFit: 'contain' }}
              onClick={e => e.stopPropagation()} />
            <button onClick={() => setPreview(false)}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X style={{ width: '18px', height: '18px' }} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const UploadModal = ({ aircraft, darkMode, onClose, onUploaded }) => {
  const { t } = useTranslation('admin');
  const [file, setFile]                   = useState(null);
  const [previewSrc, setPreviewSrc]       = useState(null);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedItem, setSelectedItem]   = useState('');
  const [uploading, setUploading]         = useState(false);
  const [linking, setLinking]             = useState(false);
  const [error, setError]                 = useState('');
  const [uploadedImg, setUploadedImg]     = useState(null);
  const [checklist, setChecklist]         = useState([]);
  const inputRef = useRef(null);

  // Cargar checklist del avión para mostrar secciones e ítems reales
  useEffect(() => {
    if (!aircraft?.id) return;
    api.get('/api/aircraft/' + aircraft.id)
      .then(r => {
        const cl = r.data?.data?.checklist || r.data?.checklist || [];
        setChecklist(cl);
        if (cl.length > 0) setSelectedSection(cl[0].section);
      })
      .catch(() => {});
  }, [aircraft?.id]);

  const currentSection = checklist.find(s => s.section === selectedSection);
  const sectionItems   = currentSection?.items || [];

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewSrc(e.target.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file || !selectedSection) { setError(t('select_image_section')); return; }
    setUploading(true); setError('');
    try {
      // 1. Subir a Cloudinary
      const dataUrl = await fileToBase64(file);
      const base64  = dataUrl.split(',')[1];
      const res = await api.post('/api/images/reference/upload', {
        image: base64, mediaType: file.type || 'image/jpeg',
        aircraftId: aircraft.id,
        sectionName: selectedSection,
        itemName: selectedItem || undefined,
      });
      const imageUrl = res.data.data?.url;

      // 2. Si seleccionó un ítem, vincular la imagen al checklist
      if (imageUrl && selectedItem && checklist.length > 0) {
        setLinking(true);
        const updatedChecklist = checklist.map(section => ({
          ...section,
          items: (section.items || []).map(item => {
            const itemText = typeof item === 'string' ? item : item.text;
            if (section.section === selectedSection && itemText === selectedItem) {
              const itemObj = typeof item === 'string' ? { text: item } : { ...item };
              itemObj.reference_image_url = imageUrl;
              return itemObj;
            }
            return item;
          })
        }));
        await api.put('/api/aircraft/' + aircraft.id + '/checklist', { checklist: updatedChecklist });
        setLinking(false);
      }

      onUploaded(res.data.data);
      setUploadedImg({ ...res.data.data, linkedTo: selectedItem || null });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al subir la imagen');
      setLinking(false);
    } finally { setUploading(false); }
  };

  const selectStyle = { width: '100%', padding: '10px 12px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.06)' : 'white', color: darkMode ? 'rgba(255,255,255,0.8)' : '#0f172a', fontSize: '13px', outline: 'none', cursor: 'pointer', fontFamily: 'inherit' };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={uploadedImg ? undefined : onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94 }}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '420px', borderRadius: '24px', background: darkMode ? '#0f1929' : 'white', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', boxShadow: '0 24px 60px rgba(0,0,0,0.4)', overflow: 'hidden', position: 'relative' }}>
        <motion.button onClick={onClose} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          style={{ position: 'absolute', top: '14px', right: '14px', zIndex: 10, width: '32px', height: '32px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <X style={{ width: '14px', height: '14px' }} />
        </motion.button>
        <div style={{ height: '4px', background: uploadedImg ? 'linear-gradient(90deg,#16a34a,#22c55e)' : 'linear-gradient(90deg,#2563eb,#7c3aed)' }} />

        {uploadedImg ? (
          <div style={{ padding: '28px 28px 32px', textAlign: 'center' }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.1 }}
              style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg,#16a34a,#22c55e)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 28px rgba(22,163,74,0.35)' }}>
              <CheckCircle2 style={{ width: '32px', height: '32px', color: 'white' }} />
            </motion.div>
            <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a' }}>
              {uploadedImg.linkedTo ? 'Imagen vinculada al ítem ✅' : 'Imagen subida correctamente'}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#64748b', lineHeight: 1.5 }}>
              {uploadedImg.linkedTo
                ? <>La imagen quedó vinculada a <strong style={{ color: darkMode ? 'white' : '#0f172a' }}>{uploadedImg.linkedTo}</strong> en la sección <strong style={{ color: darkMode ? 'white' : '#0f172a' }}>{selectedSection}</strong>. Los pilotos la verán como imagen de referencia al chequear ese ítem.</>
                : <>Guardada en Cloudinary en la sección {selectedSection}. Seleccioná un ítem la próxima vez para vincularla directamente al checklist.</>
              }
            </p>
            <img src={uploadedImg.url} alt="uploaded" style={{ width: '100%', maxHeight: '130px', objectFit: 'cover', borderRadius: '12px', marginBottom: '20px' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <motion.button onClick={() => { setUploadedImg(null); setFile(null); setPreviewSrc(null); setSelectedItem(''); }}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                style={{ flex: 1, padding: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '14px', cursor: 'pointer', background: 'transparent', color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit' }}>
                Subir otra
              </motion.button>
              <motion.button onClick={onClose} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '14px', cursor: 'pointer', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: '13px', fontFamily: 'inherit' }}>
                Cerrar
              </motion.button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '20px', paddingRight: '40px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: darkMode ? 'white' : '#0f172a' }}>Subir imagen de referencia</h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                {aircraft.manufacturer} {aircraft.model_name} {aircraft.registration ? '(' + aircraft.registration + ')' : ''}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Selector de imagen */}
              <div onClick={() => inputRef.current?.click()} style={{ borderRadius: '14px', overflow: 'hidden', cursor: 'pointer', border: '2px dashed ' + (darkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1'), minHeight: '130px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc' }}>
                <input ref={inputRef} type="file" accept="image/*" hidden onChange={e => handleFile(e.target.files?.[0])} />
                {previewSrc ? (
                  <img src={previewSrc} alt="preview" style={{ width: '100%', maxHeight: '180px', objectFit: 'contain' }} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Upload style={{ width: '24px', height: '24px', color: darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8', margin: '0 auto 8px', display: 'block' }} />
                    <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>Tocá para seleccionar imagen</p>
                  </div>
                )}
              </div>

              {/* Sección — dropdown real del checklist */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Sección *</label>
                <select value={selectedSection} onChange={e => { setSelectedSection(e.target.value); setSelectedItem(''); }} style={selectStyle}>
                  <option value="">— Seleccioná una sección —</option>
                  {checklist.map((s, i) => <option key={i} value={s.section}>{s.section}</option>)}
                </select>
              </div>

              {/* Ítem — dropdown real del checklist */}
              {selectedSection && sectionItems.length > 0 && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                    Ítem <span style={{ color: darkMode ? 'rgba(255,255,255,0.25)' : '#cbd5e1', fontWeight: 400 }}>(para vincularlo directo al checklist)</span>
                  </label>
                  <select value={selectedItem} onChange={e => setSelectedItem(e.target.value)} style={selectStyle}>
                    <option value="">— Solo guardar en la sección —</option>
                    {sectionItems.map((item, i) => {
                      const text = typeof item === 'string' ? item : item.text;
                      return <option key={i} value={text}>{text}</option>;
                    })}
                  </select>
                  {selectedItem && (
                    <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>
                      ✅ La imagen se vinculará automáticamente a este ítem
                    </p>
                  )}
                </div>
              )}

              {error && (
                <div style={{ padding: '10px 12px', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <AlertCircle style={{ width: '14px', height: '14px', color: '#ef4444', flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>{error}</p>
                </div>
              )}

              <motion.button onClick={handleUpload} disabled={uploading || linking || !file || !selectedSection}
                whileHover={!uploading && !linking && file && selectedSection ? { scale: 1.02 } : {}}
                whileTap={!uploading && !linking && file && selectedSection ? { scale: 0.97 } : {}}
                style={{ width: '100%', padding: '13px', border: 'none', borderRadius: '14px', cursor: uploading || linking || !file || !selectedSection ? 'not-allowed' : 'pointer', background: file && selectedSection ? 'linear-gradient(135deg,#2563eb,#7c3aed)' : 'rgba(0,0,0,0.1)', color: file && selectedSection ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'inherit' }}>
                {linking
                  ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} /> Vinculando al checklist...</>
                  : uploading
                  ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} /> Subiendo imagen...</>
                  : <><Upload style={{ width: '15px', height: '15px' }} /> {selectedItem ? 'Subir y vincular al ítem' : 'Subir imagen'}</>
                }
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const ChecklistPreview = ({ checklist, darkMode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
    {checklist.map((section, si) => (
      <div key={si} style={{ borderRadius: '12px', overflow: 'hidden', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}>
        <div style={{ padding: '10px 14px', background: 'linear-gradient(135deg,#2563eb,#6d28d9)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>{section.icon || '📋'}</span>
          <span style={{ fontWeight: 800, fontSize: '13px', color: 'white' }}>{section.section}</span>
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{section.items?.length || 0} items</span>
        </div>
        <div style={{ padding: '8px 0' }}>
          {(section.items || []).map((item, ii) => (
            <div key={ii} style={{ padding: '7px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', background: ii % 2 === 0 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />
              {typeof item === 'string' ? item : item.text}
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Mismas fases que el editor de checklist (AdminAircraft.js → ChecklistSection)
const PHASE_OPTIONS = [
  { value: 'prevuelo',         label: '🛫 Prevuelo' },
  { value: 'puesta_en_marcha', label: '🔑 Puesta en marcha' },
  { value: 'prueba_motor',     label: '⚙️ Prueba de motor' },
];
const phaseLabel = (phase) => PHASE_OPTIONS.find(p => p.value === phase)?.label || PHASE_OPTIONS[0].label;

const SaveSuccessModal = ({ aircraft, totalItems, sections, darkMode, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
    <motion.div initial={{ scale: 0.88, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.88 }}
      transition={{ type: 'spring', stiffness: 140, damping: 18 }}
      style={{ width: '100%', maxWidth: '380px', borderRadius: '28px', overflow: 'hidden', background: darkMode ? 'rgba(10,15,30,0.97)' : 'white', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', border: darkMode ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(34,197,94,0.25)', position: 'relative' }}>
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #16a34a, #22c55e)' }} />
      <motion.button onClick={onClose} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
        style={{ position: 'absolute', top: '14px', right: '14px', width: '32px', height: '32px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X style={{ width: '14px', height: '14px' }} />
      </motion.button>
      <div style={{ padding: '28px 28px 32px', textAlign: 'center' }}>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.1 }}
          style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, #16a34a, #22c55e)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', boxShadow: '0 8px 28px rgba(22,163,74,0.35)' }}>
          <CheckCircle2 style={{ width: '36px', height: '36px', color: 'white' }} />
        </motion.div>
        <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a' }}>Checklist guardada!</h3>
        <p style={{ margin: '0 0 20px', fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', lineHeight: 1.6 }}>
          Se cargaron <strong style={{ color: darkMode ? '#86efac' : '#16a34a' }}>{totalItems} items en {sections} secciones</strong>
          {aircraft && <> para <strong style={{ color: darkMode ? 'rgba(255,255,255,0.8)' : '#0f172a' }}>{aircraft}</strong></>}.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '24px' }}>
          <div style={{ padding: '6px 14px', borderRadius: '20px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#16a34a' }}>✅ {totalItems} items</span>
          </div>
          <div style={{ padding: '6px 14px', borderRadius: '20px', background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563eb' }}>📋 {sections} secciones</span>
          </div>
        </div>
        <motion.button onClick={onClose} whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
          style={{ width: '100%', padding: '14px', border: 'none', borderRadius: '16px', cursor: 'pointer', background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', fontWeight: 800, fontSize: '15px', fontFamily: 'inherit' }}>
          Entendido
        </motion.button>
      </div>
    </motion.div>
  </motion.div>
);

const AdminImages = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const [aircraftList, setAircraftList]           = useState([]);
  const [selectedAircraft, setSelectedAircraft]   = useState(null);
  const [images, setImages]                       = useState([]);
  const [loadingImages, setLoadingImages]         = useState(false);
  const [showUploadModal, setShowUploadModal]     = useState(false);
  const [scanMode, setScanMode]                   = useState('idle');
  const [capturedImage, setCapturedImage]         = useState(null);
  const [capturedFile, setCapturedFile]           = useState(null);
  const [scanResult, setScanResult]               = useState(null);
  const [scanError, setScanError]                 = useState(null);
  const [selectedAcId, setSelectedAcId]           = useState('');
  const [savingChecklist, setSavingChecklist]     = useState(false);
  const [saveSuccessData, setSaveSuccessData]     = useState(null);
  // ✅ NUEVO: sugerencias de imágenes con IA
  const [suggestions, setSuggestions]             = useState([]);
  const [suggestingImages, setSuggestingImages]   = useState(false);
  const [approvedImages, setApprovedImages]       = useState({});
  const [showSuggestions, setShowSuggestions]     = useState(false);
  const cameraInputRef                            = useRef(null);
  const filePickerRef                             = useRef(null);
  const [deleteImgTarget, setDeleteImgTarget]     = useState(null);
  const [deletingImg, setDeletingImg]             = useState(false);
  // ✅ NUEVO: escaneo por fase + confirmación antes de reemplazar
  const [scanPhase, setScanPhase]                 = useState('prevuelo');
  const [showSaveConfirm, setShowSaveConfirm]     = useState(false);
  const [existingPhaseCount, setExistingPhaseCount] = useState(0);
  const [loadingPhaseCount, setLoadingPhaseCount] = useState(false);

  useEffect(() => {
    aircraftService.getAll({ is_active: true })
      .then(r => setAircraftList(r.data?.data || []))
      .catch(() => {});
  }, []);

  const loadImages = async (aircraft) => {
    if (!aircraft) return;
    setLoadingImages(true);
    try {
      const res = await api.get('/api/images/reference/' + aircraft.id);
      setImages(res.data?.data || []);
    } catch { setImages([]); }
    finally { setLoadingImages(false); }
  };

  const handleSelectAircraft = (ac) => { setSelectedAircraft(ac); setImages([]); if (ac) loadImages(ac); };
  const handleDeleteClick = (img) => setDeleteImgTarget(img);
  const handleDeleteConfirm = async () => {
    if (!deleteImgTarget) return;
    setDeletingImg(true);
    try {
      await api.delete('/api/images/reference', { data: { publicId: deleteImgTarget.publicId } });
      setImages(prev => prev.filter(i => i.publicId !== deleteImgTarget.publicId));
    } catch (err) { console.error(err); }
    finally { setDeletingImg(false); setDeleteImgTarget(null); }
  };
  const handleUploaded = (newImg) => setImages(prev => [newImg, ...prev]);

  const handleImageCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => { setCapturedImage(ev.target.result); setScanMode('preview'); setScanResult(null); setScanError(null); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleScan = async () => {
    if (!capturedImage) return;
    setScanMode('scanning'); setScanError(null);
    try {
      const base64 = capturedImage.split(',')[1];
      const res = await api.post('/api/vision/scan-checklist', { image: base64, mediaType: capturedFile?.type || 'image/jpeg' });
      if (res.data.success && res.data.checklist) { setScanResult(res.data.checklist); setScanMode('result'); }
      else throw new Error('No se pudo interpretar la checklist');
    } catch (err) {
      setScanError(err.response?.data?.message || err.message || 'Error al procesar la imagen');
      setScanMode('preview');
    }
  };

  // ✅ NUEVO: sugerir imágenes para cada ítem
  const handleSuggestImages = async () => {
    if (!scanResult) return;
    setSuggestingImages(true); setSuggestions([]); setApprovedImages({}); setShowSuggestions(false);
    try {
      const items = [];
      scanResult.forEach(section => {
        (section.items || []).forEach(item => {
          items.push({ text: typeof item === 'string' ? item : item.text, section: section.section });
        });
      });
      const aircraft = aircraftList.find(a => a.id === selectedAcId);
      const aircraftModel = aircraft ? aircraft.manufacturer + ' ' + aircraft.model_name : '';
      const res = await api.post('/api/vision/suggest-images', { items, aircraftModel });
      setSuggestions(res.data.suggestions || []);
      setShowSuggestions(true);
    } catch (err) {
      setScanError(err.response?.data?.message || 'Error al buscar imagenes');
    } finally { setSuggestingImages(false); }
  };

  // ✅ NUEVO: antes de guardar, consulta cuántos ítems tiene HOY la fase
  // elegida para esa aeronave y abre el modal de confirmación. El guardado
  // real (handleSaveChecklist) solo se dispara si el admin confirma.
  const handleSaveClick = async () => {
    if (!selectedAcId || !scanResult) return;
    setLoadingPhaseCount(true);
    try {
      const r = await api.get('/api/aircraft/' + selectedAcId + '?phase=' + scanPhase);
      const cl = r.data?.data?.checklist || [];
      setExistingPhaseCount(cl.reduce((a, s) => a + (s.items?.length || 0), 0));
    } catch {
      setExistingPhaseCount(0);
    } finally {
      setLoadingPhaseCount(false);
      setShowSaveConfirm(true);
    }
  };

  const handleSaveChecklist = async () => {
    if (!selectedAcId || !scanResult) return;
    setSavingChecklist(true);
    try {
      // Aplicar imágenes aprobadas a los ítems
      let itemCounter = 0;
      const checklistWithImages = scanResult.map(section => ({
        ...section,
        items: (section.items || []).map(item => {
          const idx = itemCounter++;
          const approvedUrl = approvedImages[idx];
          const itemObj = typeof item === 'string' ? { text: item } : { ...item };
          if (approvedUrl) itemObj.reference_image_url = approvedUrl;
          return itemObj;
        })
      }));
      const res = await api.put('/api/aircraft/' + selectedAcId + '/checklist', { checklist: checklistWithImages, phase: scanPhase });
      const aircraft = aircraftList.find(a => a.id === selectedAcId);
      const aircraftName = aircraft ? aircraft.manufacturer + ' ' + aircraft.model_name + (aircraft.registration ? ' (' + aircraft.registration + ')' : '') : '';
      setSaveSuccessData({
        aircraft: aircraftName,
        totalItems: res.data?.totalItems || scanResult.reduce((a, s) => a + (s.items?.length || 0), 0),
        sections: res.data?.sections || scanResult.length,
        imagesAssigned: Object.values(approvedImages).filter(Boolean).length,
      });
    } catch (err) { setScanError(err.response?.data?.message || 'Error al guardar'); }
    finally { setSavingChecklist(false); setShowSaveConfirm(false); }
  };

  const handleSuccessClose = () => {
    setSaveSuccessData(null); setScanMode('idle'); setCapturedImage(null);
    setCapturedFile(null); setScanResult(null); setScanError(null); setSelectedAcId('');
    setSuggestions([]); setApprovedImages({}); setShowSuggestions(false);
    setScanPhase('prevuelo'); setShowSaveConfirm(false); setExistingPhaseCount(0);
  };

  const resetScanner = () => {
    setScanMode('idle'); setCapturedImage(null); setCapturedFile(null);
    setScanResult(null); setScanError(null); setSelectedAcId(''); setSaveSuccessData(null);
    setSuggestions([]); setApprovedImages({}); setShowSuggestions(false);
    setScanPhase('prevuelo'); setShowSaveConfirm(false); setExistingPhaseCount(0);
  };

  const card  = { background: darkMode ? 'rgba(255,255,255,0.03)' : 'white', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };
  const label = { fontSize: '11px', fontWeight: 800, color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a', letterSpacing: '-0.5px' }}>Imagenes de Referencia</h1>
        <p style={{ margin: 0, fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>Gestiona las imagenes del checklist y genera listas desde fotos con IA</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px' }}>
        {/* ══ LEFT: Biblioteca ════════════════════════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={card}>
            <p style={label}>Biblioteca de imagenes</p>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ ...label, marginBottom: '8px' }}>Aeronave</p>
              <select value={selectedAircraft?.id || ''} onChange={e => handleSelectAircraft(aircraftList.find(a => a.id === e.target.value) || null)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.06)' : 'white', color: darkMode ? 'rgba(255,255,255,0.8)' : '#0f172a', fontSize: '14px', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                <option value="">— Selecciona un avion —</option>
                {aircraftList.map(a => <option key={a.id} value={a.id}>{a.manufacturer} {a.model_name} {a.registration ? '(' + a.registration + ')' : ''}</option>)}
              </select>
            </div>
            {selectedAircraft ? (
              <>
                <motion.button onClick={() => setShowUploadModal(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  style={{ width: '100%', padding: '12px', border: 'none', borderRadius: '14px', cursor: 'pointer', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
                  <Upload style={{ width: '15px', height: '15px' }} /> Subir imagen de referencia
                </motion.button>
                {loadingImages ? (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      style={{ width: '28px', height: '28px', borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#2563eb', margin: '0 auto' }} />
                  </div>
                ) : images.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <FolderOpen style={{ width: '36px', height: '36px', color: darkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1', margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ margin: '0 0 4px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>Sin imagenes para esta aeronave</p>
                    <p style={{ margin: 0, fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1', fontFamily: 'monospace' }}>condor360/reference/{selectedAircraft.id}/...</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                    <AnimatePresence>
                      {images.map(img => <ImageCard key={img.publicId} img={img} onDelete={handleDeleteClick} darkMode={darkMode} />)}
                    </AnimatePresence>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 0', opacity: 0.5 }}>
                <Plane style={{ width: '36px', height: '36px', color: darkMode ? 'rgba(255,255,255,0.2)' : '#cbd5e1', margin: '0 auto 10px', display: 'block' }} />
                <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>Selecciona una aeronave para ver sus imagenes</p>
              </div>
            )}
          </div>
        </div>

        {/* ══ RIGHT: AI Scanner ════════════════════════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'linear-gradient(135deg,#7c3aed,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Wand2 style={{ width: '18px', height: '18px', color: 'white' }} />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 800, fontSize: '15px', color: darkMode ? 'white' : '#0f172a' }}>Generador con IA</p>
                <p style={{ margin: 0, fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b' }}>Saca una foto a cualquier checklist impresa</p>
              </div>
            </div>

            {scanMode === 'idle' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <p style={{ ...label, marginBottom: '8px' }}>¿Qué lista escaneás?</p>
                  <select value={scanPhase} onChange={e => setScanPhase(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.06)' : 'white', color: darkMode ? 'rgba(255,255,255,0.8)' : '#0f172a', fontSize: '14px', fontWeight: 600, cursor: 'pointer', outline: 'none', marginBottom: '6px' }}>
                    {PHASE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handleImageCapture} />
                <input ref={filePickerRef} type="file" accept="image/*" hidden onChange={handleImageCapture} />
                <motion.button onClick={() => cameraInputRef.current?.click()} whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
                  style={{ width: '100%', padding: '18px', border: 'none', borderRadius: '16px', cursor: 'pointer', background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)', color: 'white', fontWeight: 800, fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 8px 28px rgba(109,40,217,0.35)' }}>
                  <Camera style={{ width: '20px', height: '20px' }} /> Abrir camara
                </motion.button>
                <motion.button onClick={() => filePickerRef.current?.click()} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  style={{ width: '100%', padding: '14px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '16px', cursor: 'pointer', background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc', color: darkMode ? 'rgba(255,255,255,0.6)' : '#374151', fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Image style={{ width: '16px', height: '16px' }} /> Subir imagen existente
                </motion.button>
                <div style={{ marginTop: '8px', padding: '14px', borderRadius: '14px', background: darkMode ? 'rgba(109,40,217,0.08)' : 'rgba(109,40,217,0.04)', border: darkMode ? '1px solid rgba(109,40,217,0.2)' : '1px solid rgba(109,40,217,0.12)' }}>
                  <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 700, color: darkMode ? '#a78bfa' : '#7c3aed' }}>✨ Como funciona?</p>
                  {['Saca una foto a la checklist impresa del avion', 'Claude Vision lee y estructura el contenido', 'Opcionalmente busca imagenes de referencia con IA', 'Revisas y guardas directo en la aeronave'].map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: i < 3 ? '5px' : 0 }}>
                      <span style={{ width: '18px', height: '18px', borderRadius: '6px', background: 'linear-gradient(135deg,#7c3aed,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 900, color: 'white', flexShrink: 0 }}>{i+1}</span>
                      <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', lineHeight: 1.4 }}>{s}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {(scanMode === 'preview' || scanMode === 'scanning') && capturedImage && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <img src={capturedImage} alt="Captura" style={{ width: '100%', maxHeight: '260px', objectFit: 'contain', borderRadius: '14px', background: '#0f172a' }} />
                {scanError && (
                  <div style={{ padding: '12px', borderRadius: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: '8px' }}>
                    <AlertCircle style={{ width: '15px', height: '15px', color: '#ef4444', flexShrink: 0, marginTop: '1px' }} />
                    <p style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>{scanError}</p>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <motion.button onClick={handleScan} disabled={scanMode === 'scanning'}
                    style={{ flex: 1, padding: '13px', border: 'none', borderRadius: '14px', cursor: scanMode === 'scanning' ? 'not-allowed' : 'pointer', background: scanMode === 'scanning' ? 'rgba(109,40,217,0.5)' : 'linear-gradient(135deg,#7c3aed,#2563eb)', color: 'white', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    {scanMode === 'scanning'
                      ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} /> Analizando...</>
                      : <><Sparkles style={{ width: '16px', height: '16px' }} /> Analizar con IA</>
                    }
                  </motion.button>
                  <motion.button onClick={resetScanner} whileTap={{ scale: 0.97 }}
                    style={{ padding: '13px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '14px', cursor: 'pointer', background: 'transparent', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', display: 'flex', alignItems: 'center' }}>
                    <X style={{ width: '16px', height: '16px' }} />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {scanMode === 'result' && scanResult && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ padding: '12px 14px', borderRadius: '14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Sparkles style={{ width: '15px', height: '15px', color: '#22c55e', flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#22c55e' }}>
                    Checklist generada! — {scanResult.reduce((a, s) => a + (s.items?.length || 0), 0)} items en {scanResult.length} secciones
                  </p>
                </div>

                <div>
                  <p style={{ ...label, marginBottom: '8px' }}>¿Qué lista escaneás?</p>
                  <select value={scanPhase} onChange={e => setScanPhase(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.06)' : 'white', color: darkMode ? 'rgba(255,255,255,0.8)' : '#0f172a', fontSize: '14px', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                    {PHASE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>

                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <ChecklistPreview checklist={scanResult} darkMode={darkMode} />
                </div>

                <div>
                  <p style={{ ...label, marginBottom: '8px' }}>Asignar a aeronave</p>
                  <select value={selectedAcId} onChange={e => setSelectedAcId(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.06)' : 'white', color: darkMode ? 'rgba(255,255,255,0.8)' : '#0f172a', fontSize: '14px', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                    <option value="">— Selecciona un avion —</option>
                    {aircraftList.map(a => <option key={a.id} value={a.id}>{a.manufacturer} {a.model_name} {a.registration ? '(' + a.registration + ')' : ''}</option>)}
                  </select>
                </div>

                {/* ✅ NUEVO: botón sugerir imágenes (opcional) */}
                {selectedAcId && !showSuggestions && (
                  <motion.button onClick={handleSuggestImages} disabled={suggestingImages}
                    whileHover={!suggestingImages ? { scale: 1.02 } : {}} whileTap={!suggestingImages ? { scale: 0.97 } : {}}
                    style={{ width: '100%', padding: '11px', border: darkMode ? '1px solid rgba(109,40,217,0.35)' : '1px solid rgba(109,40,217,0.25)', borderRadius: '14px', cursor: suggestingImages ? 'not-allowed' : 'pointer', background: darkMode ? 'rgba(109,40,217,0.1)' : 'rgba(109,40,217,0.05)', color: darkMode ? '#a78bfa' : '#7c3aed', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'inherit' }}>
                    {suggestingImages
                      ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#7c3aed' }} /> Buscando imagenes en Wikimedia...</>
                      : <><Sparkles style={{ width: '14px', height: '14px' }} /> ✨ Sugerir imagenes con IA (opcional)</>
                    }
                  </motion.button>
                )}

                {/* ✅ NUEVO: panel de sugerencias para aprobar */}
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{ borderRadius: '14px', border: darkMode ? '1px solid rgba(109,40,217,0.3)' : '1px solid rgba(109,40,217,0.2)', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: darkMode ? 'rgba(109,40,217,0.12)' : 'rgba(109,40,217,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Sparkles style={{ width: '13px', height: '13px', color: '#7c3aed' }} />
                        <span style={{ fontSize: '12px', fontWeight: 700, color: darkMode ? '#a78bfa' : '#7c3aed' }}>
                          {Object.values(approvedImages).filter(Boolean).length} imagenes seleccionadas · toca para aprobar
                        </span>
                      </div>
                      <button onClick={() => setShowSuggestions(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8', display: 'flex', alignItems: 'center' }}>
                        <X style={{ width: '13px', height: '13px' }} />
                      </button>
                    </div>
                    <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '280px', overflowY: 'auto' }}>
                      {suggestions.map((sug) => (
                        <div key={sug.index}>
                          <p style={{ margin: '0 0 5px', fontSize: '11px', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.45)' : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sug.itemText}
                          </p>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            {sug.images.map((img, ii) => {
                              const isSelected = approvedImages[sug.index] === img.url;
                              return (
                                <motion.div key={ii}
                                  onClick={() => setApprovedImages(prev => ({ ...prev, [sug.index]: isSelected ? undefined : img.url }))}
                                  whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
                                  style={{ flex: 1, aspectRatio: '1', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', border: isSelected ? '2.5px solid #22c55e' : '2px solid ' + (darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'), position: 'relative', background: darkMode ? '#1e293b' : '#f8fafc' }}>
                                  <img src={img.url} alt={img.title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={e => { e.target.parentElement.style.display = 'none'; }} />
                                  {isSelected && (
                                    <div style={{ position: 'absolute', top: '3px', right: '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Check style={{ width: '9px', height: '9px', color: 'white' }} />
                                    </div>
                                  )}
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {scanError && (
                  <div style={{ padding: '10px 12px', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>{scanError}</p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <motion.button onClick={handleSaveClick} disabled={!selectedAcId || savingChecklist || loadingPhaseCount}
                    whileHover={selectedAcId ? { scale: 1.02 } : {}} whileTap={selectedAcId ? { scale: 0.97 } : {}}
                    style={{ flex: 1, padding: '13px', border: 'none', borderRadius: '14px', cursor: selectedAcId && !savingChecklist && !loadingPhaseCount ? 'pointer' : 'not-allowed', background: selectedAcId ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'rgba(0,0,0,0.1)', color: selectedAcId ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    {savingChecklist || loadingPhaseCount
                      ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} /> {loadingPhaseCount ? 'Verificando...' : 'Guardando...'}</>
                      : <><Save style={{ width: '15px', height: '15px' }} /> Guardar checklist{Object.values(approvedImages).filter(Boolean).length > 0 ? ' + ' + Object.values(approvedImages).filter(Boolean).length + ' imgs' : ''}</>
                    }
                  </motion.button>
                  <motion.button onClick={resetScanner} whileTap={{ scale: 0.97 }}
                    style={{ padding: '13px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '14px', cursor: 'pointer', background: 'transparent', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600 }}>
                    <RefreshCw style={{ width: '14px', height: '14px' }} /> Nueva
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showUploadModal && selectedAircraft && (
          <UploadModal aircraft={selectedAircraft} darkMode={darkMode} onClose={() => setShowUploadModal(false)} onUploaded={handleUploaded} />
        )}
        {saveSuccessData && (
          <SaveSuccessModal aircraft={saveSuccessData.aircraft} totalItems={saveSuccessData.totalItems} sections={saveSuccessData.sections} darkMode={darkMode} onClose={handleSuccessClose} />
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={!!deleteImgTarget}
        title={t('confirm_modal_image_delete_title')}
        message={t('confirm_modal_image_delete_message')}
        confirmLabel={t('confirm_modal_default_confirm_delete')}
        variant="danger"
        isLoading={deletingImg}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteImgTarget(null)}
        darkMode={darkMode}
      />

      {/* ✅ NUEVO: confirmación antes de reemplazar los ítems de la fase escaneada */}
      <ConfirmModal
        isOpen={showSaveConfirm}
        title={`Guardar checklist — ${phaseLabel(scanPhase)}`}
        message={
          existingPhaseCount > 0
            ? `Esto reemplazará los ${existingPhaseCount} ítems actuales de la fase ${phaseLabel(scanPhase)} por los ${scanResult ? scanResult.reduce((a, s) => a + (s.items?.length || 0), 0) : 0} ítems detectados. Las demás fases no se modifican.`
            : `Se agregarán ${scanResult ? scanResult.reduce((a, s) => a + (s.items?.length || 0), 0) : 0} ítems a la fase ${phaseLabel(scanPhase)}. Las demás fases no se modifican.`
        }
        confirmLabel="Confirmar y guardar"
        cancelLabel="Cancelar"
        variant={existingPhaseCount > 0 ? 'warning' : 'info'}
        isLoading={savingChecklist}
        onConfirm={handleSaveChecklist}
        onCancel={() => setShowSaveConfirm(false)}
        darkMode={darkMode}
      />
    </div>
  );
};

export default AdminImages;
